import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import {
  DEFAULT_DATE_RANGE,
  GROUP_COLORS,
  secondsToRange,
} from "portal/utils/reports";
import { isInstance, isReport, type ReportTableProps } from "./table.types";
import { Metric } from "portal/utils/metrics";
import { METRIC_WEEDING_TIME } from "portal/utils/certifiedMetrics";
import { range } from "portal/utils/arrays";
import { ReportResponse } from "protos/portal/reports";
import { TFunction } from "i18next";

export const getVisibleColumns = (
  input: ReportTableProps
): ReportResponse["visibleColumns"] => {
  if (isInstance(input)) {
    return input.instance?.visibleColumns ?? [];
  } else if (isReport(input)) {
    return input.report?.visibleColumns ?? [];
  } else {
    return [];
  }
};

export const toCertifiedFields = (visibleMetrics: Metric[]): string[] => {
  // Reports table skips day rows without weeding uptime -- so we will always ask for it regardless of user selection
  return [
    ...new Set([...visibleMetrics.map((m) => m.id), METRIC_WEEDING_TIME.id]),
  ];
};

export const getName = (t: TFunction, input: ReportTableProps): string => {
  if (isInstance(input)) {
    return (
      input.instance?.name ?? t("views.reports.scheduled.table.unknownReport")
    );
  } else if (isReport(input)) {
    return (
      input.report?.name ?? t("views.reports.scheduled.table.unknownReport")
    );
  } else {
    return t("views.reports.scheduled.table.unknownReport");
  }
};

export const getShowTotal = (
  input: ReportTableProps
): ReportResponse["showTotal"] => {
  if (isInstance(input)) {
    return input.instance?.showTotal ?? false;
  } else if (isReport(input)) {
    return input.report?.showTotal ?? false;
  } else {
    return false;
  }
};

export const getShowAverage = (
  input: ReportTableProps
): ReportResponse["showAverage"] => {
  if (isInstance(input)) {
    return input.instance?.showAverage ?? false;
  } else if (isReport(input)) {
    return input.report?.showAverage ?? false;
  } else {
    return false;
  }
};

export const getRobotIds = (
  input: ReportTableProps
): ReportResponse["robotIds"] => {
  if (isInstance(input)) {
    return input.instance?.robotIds ?? [];
  } else if (isReport(input)) {
    return input.report?.robotIds ?? [];
  } else {
    return [];
  }
};

export const getDateRange = (input: ReportTableProps): DateRange<DateTime> => {
  const dateRange = DEFAULT_DATE_RANGE;
  let startDate: number;
  let endDate: number;
  if (isInstance(input)) {
    if (!input.instance) {
      return dateRange;
    }
    ({ startDate, endDate } = input.instance);
  } else if (isReport(input)) {
    if (!input.report) {
      return dateRange;
    }
    ({ startDate, endDate } = input.report);
  } else {
    return dateRange;
  }
  return secondsToRange(startDate, endDate);
};

export const getRobotColorClass = (
  selectedRobotIds: number[],
  robotId: number
): string => {
  let colorIndex = selectedRobotIds.indexOf(robotId);
  while (colorIndex + 1 > GROUP_COLORS.length) {
    colorIndex -= GROUP_COLORS.length;
  }
  return GROUP_COLORS[colorIndex] ?? "";
};

export const getDatesFromRange = (dateRange: DateRange<DateTime>): string[] => {
  const [startDate, endDate] = dateRange;
  if (!startDate || !endDate) {
    return [];
  }
  const dayCount = Math.floor(
    (endDate.diff(startDate, "days").toObject().days ?? 0) + 1
  );
  return range(dayCount).map((dayOffset) =>
    startDate.plus({ days: dayOffset }).toFormat(DATE_PATH_FORMAT)
  );
};
